import axios from 'axios';
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { isEmpty, pickBy, equals, compose, assoc, path } from 'ramda';
import Cookies from 'universal-cookie';

import * as Config from '../../config/config';
import {
  LocalStorage,
  migrateExistingUser,
  migrateExistingUserForgotPassword,
  redirectWindowToo,
  flattenObjectToDotNotation,
  createRedirectUrl,
  retrieveUriParam,
  updateUserLastAction,
  updateUserClaims,
  resendTempPassword,
  updateUsersTokens
} from '../../utils/helpers';

const cookies = new Cookies();

/**
 * Returns userProfile as Object from LocalStorage
 */
export const getProfileFromLocalStorage = () => LocalStorage.getObject('userProfile');

/**
 * Object :: Array
 * takes an object and sets keys and value in LocalStorage as individual setter
 * returns an array with the LocalStorage mutation for each key
 * @param {Object} obj
 */
export const setIndividuallyInLocalStorage = obj =>
  Object.keys(obj).map(e => LocalStorage.set(e, obj[e]));

/**
 * takes an object of token key-value pairs and stores them as the value to a username key
 * in LocalStorage under the 'usersTokens' item
 * @param {Object} tokens
 * @param {String} Username
 */
export const setTokensForUser = (tokens, username) => {
  const previousTokens = LocalStorage.getObject('usersTokens');
  const newTokens = { ...previousTokens, [username]: tokens };
  LocalStorage.set(`usersTokens`, JSON.stringify(newTokens));
};

export const setTokensInCookie = ({ accessToken, idToken, refreshToken }) => {
  cookies.set('turnstile-access-token', accessToken, {
    domain: Config.DOMAIN
  });
  cookies.set('turnstile-id-token', idToken, {
    domain: Config.DOMAIN
  });
  cookies.set('turnstile-refresh-token', refreshToken, {
    domain: Config.DOMAIN
  });
  cookies.set('marketplace', idToken, {
    sameSite: 'none',
    secure: true,
    domain: Config.DOMAIN
  });
};

/**
 * Cognito user pool factory function
 * returns a Cognito user pool instance
 */
export const createCognitoUserPool = () =>
  new CognitoUserPool({
    UserPoolId: Config.USER_POOL_ID,
    ClientId: Config.CLIENT_ID
  });

/**
 * String:: CognitoUser
 * CognitoUSer factory function. Takes a Username
 * returns a CognitoUser instance
 * @param {String} Username
 */
export const createCognitoUser = Username =>
  new CognitoUser({
    Username: Username.toLowerCase(),
    Pool: new CognitoUserPool({
      UserPoolId: Config.USER_POOL_ID,
      ClientId: Config.CLIENT_ID
    })
  });

/**
 * Object :: Promise<Object>
 * Hits the Cognito Authentication Service.
 * Returns a Promise.
 * on success promise will resolve to an object { userTokens::Object, newPasswordRequired::Boolean }
 *
 * on failure yields the error message provided by cognito
 * @param {Object} credentials
 * @param {Object} credentials.username username
 * @param {Object} credentials.password password
 */
export const signUserIn = ({
  username: Username,
  password: Password,
  firstName,
  lastName,
  new_password: newPassword,
  params,
  isNew
}) =>
  new Promise((resolve, reject) => {
    const extras = {
      clientId: retrieveUriParam('client_id'),
      redirectUri: retrieveUriParam('redirect_uri'),
      params: params || {},
      email: Username
    };
    const authDetails = new AuthenticationDetails({ Username, Password });
    const cognitoUser = createCognitoUser(Username);
    updateUserLastAction(extras).then(() => {
      const cognitoCallbacks = {
        onSuccess: result => {
          const idToken = result.getIdToken().getJwtToken();
          const refreshToken = result.getRefreshToken().getToken();
          const accessToken = result.getAccessToken().getJwtToken();
          updateUserClaims({
            clientId: retrieveUriParam('client_id'),
            email: Username
          });
          updateUsersTokens({
            email: Username,
            idToken,
            accessToken,
            refreshToken
          });
          resolve({
            userTokens: {
              idToken,
              refreshToken,
              accessToken
            },
            newPasswordRequired: false,
            emailVerification: isNew
          });
        },
        onFailure: err => {
          if (err.code === 'UserNotFoundException') {
            return migrateExistingUser({
              email: Username,
              password: Password,
              firstName,
              lastName,
              params,
              redirectUri: retrieveUriParam('redirect_uri'),
              isNew
            })
              .then(res => {
                if (res.status === 200 && !isNew) {
                  cognitoUser.authenticateUser(authDetails, cognitoCallbacks);
                } else {
                  resolve({
                    emailVerification: true
                  });
                }
              })
              .catch(e => {
                reject(e.message);
              });
          } else if (err.code === 'NotAuthorizedException' && isNew) {
            return migrateExistingUser({
              email: Username,
              password: Password,
              firstName,
              lastName,
              params,
              redirectUri: retrieveUriParam('redirect_uri'),
              isNew
            })
              .then(() =>
                resolve({
                  emailVerification: true
                })
              )
              .catch(() =>
                resolve({
                  emailVerification: true
                })
              );
          }

          return reject(err.message);
        },
        newPasswordRequired: userAttributes => {
          if (isEmpty(newPassword)) {
            resolve({ userTokens: {}, newPasswordRequired: true });
          } else {
            cognitoUser.completeNewPasswordChallenge(
              newPassword,
              pickBy((v, k) => !equals(k, 'email_verified'), userAttributes),
              cognitoCallbacks
            );
          }
        }
      };

      cognitoUser.authenticateUser(authDetails, cognitoCallbacks);
    });
  });

/**
 * Object :: Promise<Object>
 * Hits the Cognito Authentication Service to initiate forgot password flow.
 * Returns a Promise.
 * on success promise will resolve to an object { data::Object }
 *
 * on failure yields the error message provided by Cognito
 * @param {Object} credentials
 * @param {Object} credentials.username username
 */
export const resetPassword = ({ username: Username }) =>
  new Promise((resolve, reject) => {
    const cognitoUser = createCognitoUser(Username);

    if (cognitoUser) {
      cognitoUser.forgotPassword({
        onSuccess: data => {
          updateUserLastAction({
            clientId: retrieveUriParam('client_id'),
            redirectUri: retrieveUriParam('redirect_uri'),
            email: Username
          });
          resolve({ passwordReset: true, data });
        },
        onFailure: err => {
          if (err.code === 'UserNotFoundException') {
            return migrateExistingUserForgotPassword(Username)
              .then(res => {
                if (res.status === 200) {
                  updateUserLastAction({
                    clientId: retrieveUriParam('client_id'),
                    redirectUri: retrieveUriParam('redirect_uri'),
                    email: Username
                  });
                  resolve({ passwordReset: true, res });
                }
              })
              .catch(e => {
                reject(e.message);
              });
          } else if (err.code === 'NotAuthorizedException') {
            return resendTempPassword({
              email: Username,
              clientId: retrieveUriParam('client_id'),
              redirectUri: retrieveUriParam('redirect_uri')
            })
              .then(res => {
                resolve({ passwordReset: true, res });
              })
              .catch(() => {
                reject(err.message);
              });
          }

          return reject(err.message);
        }
      });
    } else {
      reject(cognitoUser.message);
    }
  });

/**
 * Object :: Promise<Object>
 * Sends a new password confirmation request to Cognito
 * Returns a Promise.
 * on success promise will resolve
 * on failure yields the error message provided by Cognito
 * @param {Object} credentials
 * @param {Object} credentials.username username
 * @param {Object} credentials.password password
 */
export const confirmNewPassword = ({ username: Username, password }) =>
  new Promise((resolve, reject) => {
    const cognitoUser = createCognitoUser(Username);

    if (cognitoUser) {
      const verificationCode = retrieveUriParam('code');

      cognitoUser.confirmPassword(verificationCode, password, {
        onSuccess: () => resolve(),
        onFailure: err => reject(err.message)
      });
    } else {
      reject(cognitoUser.message);
    }
  });

/**
 * String :: String
 * Retrieves last redirectUri for user from API by email
 * Returns a the redirectUri as a string
 * @param {String} email
 */
export const fetchRedirectUri = async email => {
  const options = {
    method: 'get',
    url: `${Config.AUTH_API_BASE_URL}/users?email=${encodeURIComponent(email.toLowerCase())}`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    }
  };
  const res = await axios(options);

  return (
    res && res.data[0] && res.data[0].lastActionAttempt && res.data[0].lastActionAttempt.redirectUri
  );
};

export const verifyEmailAddress = async (email, params) => {
  const options = {
    method: 'get',
    url: `${Config.AUTH_API_BASE_URL}/authn/verify-email`,
    params: {
      email: email.toLowerCase(),
      ...params
    },
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    }
  };
  const res = await axios(options);
  return path(['data'], res);
};
export const verifyAccessToken = async accessToken => {
  const options = {
    method: 'get',
    url: `${Config.AUTH_API_BASE_URL}/authn/verify-session`,
    params: {
      token: accessToken
    },
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    }
  };
  const { status } = await axios(options);
  return status === 200;
};

/**
 * Object ::
 * takes an object of user info and a redirect uri
 * configures localStorage with tokens and redirects user
 * @param {Object} info
 * @param {Object} info.userTokens userTokens
 * @param {String} info.redirectUri redirectUri
 * @param {String} info.username username(email)
 */
export const logUserIn = ({ userTokens, redirectUri, username }) => {
  compose(setIndividuallyInLocalStorage, flattenObjectToDotNotation)({ userTokens }, username);
  setTokensForUser(userTokens, username.toLowerCase());
  setTokensInCookie(userTokens);
  if (redirectUri) {
    compose(redirectWindowToo, createRedirectUrl, assoc('redirectUri', redirectUri))(userTokens);
  }
};

export const checkUserSession = async () => {
  const usersTokens = LocalStorage.getObject('usersTokens');
  const localUsers = LocalStorage.getArray('usernames');

  const promises = localUsers.map(email => {
    if (usersTokens[email]) {
      const options = {
        method: 'get',
        url: `${Config.AUTH_API_BASE_URL}/authn/verify-session?token=${
          usersTokens[email].accessToken
        }`,
        headers: {
          'X-API-KEY': Config.AUTH_API_KEY
        }
      };

      return new Promise(resolve => {
        axios(options)
          .then(() => resolve({ email }))
          .catch(err => resolve({ email, error: err }));
      });
    }
    return null;
  });

  const results = await Promise.all(promises);

  results.forEach(result => {
    if (result && result.error) delete usersTokens[result.email.toLowerCase()];
  });

  LocalStorage.setObject('usersTokens', usersTokens);
  return usersTokens;
};

export const sendAuthLink = async ({ email, clientId, redirectUri, params }) => {
  const options = {
    method: 'post',
    url: `${Config.AUTH_API_BASE_URL}/authn/login-link`,
    data: {
      email: email.toLowerCase(),
      client_id: clientId,
      redirect_uri: redirectUri,
      params
    },
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    }
  };
  const { data, status } = await axios(options);
  return status === 200
    ? data
    : {
        error: new Error('An error occured please try again.')
      };
};



// WEBPACK FOOTER //
// ./src/core/auth/util.js